import { diffLines } from 'diff'
import { GitBranch, GitCommitHorizontal, GitFork, Plus } from 'lucide-react'
import type { BranchRecord, VersionCommit } from '../core/store'

interface VersionsPanelProps {
  branches: BranchRecord[]
  commits: VersionCommit[]
  activeBranchId: string
  currentCode: string
  onRemix: () => void
}

function relativeTime(timestamp: number): string {
  const minutes = Math.max(1, Math.round((Date.now() - timestamp) / 60000))
  return minutes < 60 ? `${minutes}m` : `${Math.round(minutes / 60)}h`
}

export function VersionsPanel({
  branches,
  commits,
  activeBranchId,
  currentCode,
  onRemix,
}: VersionsPanelProps) {
  const active = branches.find((branch) => branch.id === activeBranchId) ?? branches[0]
  const changes = diffLines(active.baseCode, currentCode)
  const additions = changes
    .filter((change) => change.added)
    .reduce((count, change) => count + (change.count ?? 0), 0)
  const removals = changes
    .filter((change) => change.removed)
    .reduce((count, change) => count + (change.count ?? 0), 0)

  return (
    <div className="versions-content">
      <section className="version-hero">
        <span className="eyebrow-label">Active lineage</span>
        <div className="branch-name"><GitBranch size={14} /> {active.name}</div>
        <p>Remixed from {active.parentId ?? 'an empty canvas'} · attribution preserved</p>
        <button type="button" onClick={onRemix}>
          <GitFork size={13} /> Remix current <Plus size={11} />
        </button>
      </section>

      <section className="branch-map" aria-label="Branch lineage">
        {branches.map((branch, index) => (
          <div
            className={`branch-row ${branch.id === activeBranchId ? 'is-active' : ''}`}
            key={branch.id}
          >
            <div className="branch-rail">
              <i style={{ background: branch.color }} />
              {index < branches.length - 1 ? <span /> : null}
            </div>
            <div>
              <strong>{branch.name}</strong>
              <small>{branch.author} · {relativeTime(branch.createdAt)}</small>
            </div>
          </div>
        ))}
      </section>

      <section className="diff-section">
        <div className="section-title-row">
          <span>Working diff</span>
          <span className="diff-count"><b>+{additions}</b><i>−{removals}</i></span>
        </div>
        <div className="mini-diff" aria-label="Code changes from branch base">
          {changes.map((change, changeIndex) =>
            change.value
              .split('\n')
              .slice(0, -1)
              .map((line, lineIndex) => (
                <code
                  className={change.added ? 'added' : change.removed ? 'removed' : ''}
                  key={`${changeIndex}-${lineIndex}`}
                >
                  <span>{change.added ? '+' : change.removed ? '−' : ' '}</span>
                  {line || ' '}
                </code>
              )),
          )}
        </div>
      </section>

      <section className="commit-list">
        <div className="section-title-row"><span>Checkpoints</span></div>
        {[...commits].reverse().slice(0, 5).map((commit) => (
          <div className="commit-row" key={commit.id}>
            <GitCommitHorizontal size={13} />
            <div><strong>{commit.message}</strong><small>{commit.id} · {relativeTime(commit.at)}</small></div>
          </div>
        ))}
      </section>
    </div>
  )
}
